import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { Typography } from '@rbx/ui';
import { useLocalization, useTranslation } from '@rbx/intl';
import { defaultLocale } from '@modules/localization/constants/translationResourceConstants';
import useResponsiveDesign from '@modules/miscellaneous/common/hooks/useResponsiveDesign';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { getCurrentPlatform, Platform } from '@rbx/core';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import UseStudioButton from '@modules/useStudio/components/UseStudioButton';
import getAssistantGuideLinkForLocale from '../utils/getAssistantGuideLink';
import useNullStateStyles from './NullState.styles';

const NullStateIntro: FC<React.PropsWithChildren> = () => {
  const { locale } = useLocalization();
  const { translateHTML } = useTranslation();
  const {
    classes: { intro },
  } = useNullStateStyles();
  const { isDesktop } = useResponsiveDesign();
  const onOpenGuide = useCallback(() => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsAssistantEvent.UserClickedNullStateIntroGuideLink,
      parameters: {
        locale: locale ? String(locale) : '',
      },
    });
  }, [locale]);
  const getIsPlatformStudioCompatible = useCallback(() => {
    const currentPlatform = getCurrentPlatform();
    // TODO (@ahua, CRK-334): reuse grasshopper studio lib for studio
    // compatibility check
    return currentPlatform === Platform.Windows || currentPlatform === Platform.macOS;
  }, []);
  const introText = useMemo(() => {
    const showStudioLink = isDesktop && getIsPlatformStudioCompatible();
    let translationKey = '';
    const translationTags = [
      {
        opening: 'guideLinkStart',
        closing: 'guideLinkEnd',
        // eslint-disable-next-line react/no-unstable-nested-components
        content: (chunks: ReactNode) => (
          <DynamicallyRouted.Link
            target='_blank'
            onClick={onOpenGuide}
            href={getAssistantGuideLinkForLocale(locale ?? defaultLocale)}>
            {chunks}
          </DynamicallyRouted.Link>
        ),
      },
    ];
    unifiedLoggerClient.logImpressionEvent({
      eventName: DocsAssistantEvent.NullStateIntroGuideLink,
      parameters: {
        locale: locale ? String(locale) : '',
      },
    });
    if (showStudioLink) {
      translationTags.push({
        opening: 'studioLinkStart',
        closing: 'studioLinkEnd',
        // eslint-disable-next-line react/no-unstable-nested-components
        content: (chunks: ReactNode) => <UseStudioButton variant='link'>{chunks}</UseStudioButton>,
      });
      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsAssistantEvent.NullStateIntroStudioLink,
        parameters: {
          locale: locale ? String(locale) : '',
        },
      });
      translationKey = 'NullState.DescriptionV2';
    } else {
      translationKey = 'NullState.DescriptionV2NoStudioLink';
    }
    return translateHTML(translationKey, translationTags);
  }, [locale, onOpenGuide, getIsPlatformStudioCompatible, isDesktop, translateHTML]);
  return (
    <Typography className={intro} variant='body1' component='p' marginBottom={2}>
      {introText}
    </Typography>
  );
};

NullStateIntro.displayName = 'NullStateIntro';

export default React.memo(NullStateIntro);
